import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { Lock, Plus, Trash2, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import DbTableCatalogue, { tablePurpose } from '../../components/DbTableCatalogue';
import { useUiLocale } from '../../i18n/useUiLocale';
import { resolveSettingsNav } from '../../navigation/settingsNavigation';

const DB_SETTING_KEY = 'dbSettingUnlocked';
const DB_SETTING_TTL_MS = 10 * 60 * 1000; // 10 min

// Modal form for Add/Edit row (structure = columns from API, mode = 'add' | 'edit', initialValues = row for edit)
function RowFormModal({ tableName, structure, mode, initialValues, onSave, onClose }) {
  const pkCols = (structure || []).filter((c) => c.COLUMN_KEY === 'PRI').map((c) => c.COLUMN_NAME);
  const autoCols = (structure || []).filter((c) => (c.EXTRA || '').includes('auto_increment')).map((c) => c.COLUMN_NAME);
  const [values, setValues] = useState(() => {
    const v = {};
    (structure || []).forEach((c) => {
      const name = c.COLUMN_NAME;
      if (initialValues && Object.prototype.hasOwnProperty.call(initialValues, name)) {
        const x = initialValues[name];
        if (x == null) v[name] = '';
        else if (typeof x === 'object' && x instanceof Date) v[name] = x.toISOString().slice(0, 19);
        else if (typeof x === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(x)) v[name] = x.slice(0, 19).replace(' ', 'T');
        else v[name] = String(x);
      } else v[name] = '';
    });
    return v;
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (col, value) => {
    setValues((prev) => ({ ...prev, [col]: value }));
  };

  const inputType = (col) => {
    const dt = (col.DATA_TYPE || '').toLowerCase();
    if (dt.includes('int') || dt === 'decimal' || dt === 'float' || dt === 'double') return 'number';
    if (dt === 'date') return 'date';
    if (dt === 'datetime' || dt === 'timestamp') return 'datetime-local';
    if (dt === 'text' || dt === 'longtext' || dt === 'mediumtext') return 'textarea';
    return 'text';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {};
      (structure || []).forEach((c) => {
        const name = c.COLUMN_NAME;
        const v = values[name];
        if (v === '' || v === undefined) body[name] = null;
        else if (inputType(c) === 'number') body[name] = v === '' ? null : Number(v);
        else body[name] = v;
      });
      await onSave(body);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cols = (structure || []).filter((c) => mode !== 'add' || !autoCols.includes(c.COLUMN_NAME));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{mode === 'add' ? 'Add row' : 'Edit row'} — {tableName}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {cols.map((c) => {
            const name = c.COLUMN_NAME;
            const isPk = pkCols.includes(name);
            const isAuto = autoCols.includes(name);
            const readOnly = mode === 'edit' && isPk;
            const type = inputType(c);
            const val = values[name];
            return (
              <div key={name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {name} {c.IS_NULLABLE === 'NO' && mode === 'add' && !isAuto && <span className="text-red-500">*</span>}
                </label>
                {type === 'textarea' ? (
                  <textarea
                    value={val}
                    onChange={(e) => handleChange(name, e.target.value)}
                    readOnly={readOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    rows={3}
                  />
                ) : (
                  <input
                    type={type}
                    value={val}
                    onChange={(e) => handleChange(name, type === 'number' ? e.target.value : e.target.value)}
                    readOnly={readOnly}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                    step={type === 'number' ? 'any' : undefined}
                  />
                )}
              </div>
            );
          })}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DbSetting() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const locale = useUiLocale();
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [choiceMode, setChoiceMode] = useState(null); // 'data' | 'structure'
  const [tableData, setTableData] = useState([]);
  const [tableStructure, setTableStructure] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [backupDone, setBackupDone] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);

  const [tableFilter, setTableFilter] = useState('');

  const canDb =
    user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER';

  useEffect(() => {
    if (!canDb) {
      navigate('/settings');
      return;
    }
    const raw = sessionStorage.getItem(DB_SETTING_KEY);
    if (raw) {
      try {
        const { at } = JSON.parse(raw);
        if (Date.now() - at < DB_SETTING_TTL_MS) setUnlocked(true);
        else sessionStorage.removeItem(DB_SETTING_KEY);
      } catch (_) {
        sessionStorage.removeItem(DB_SETTING_KEY);
      }
    }
  }, [canDb, navigate]);

  useEffect(() => {
    if (!canDb) return;
    setTablesLoading(true);
    api
      .get('/settings/db/tables')
      .then((res) => setTables(res.data.data || []))
      .catch((err) => {
        toast.error(err.response?.data?.error || 'Failed to load table list');
        if (err.response?.status === 401) sessionStorage.removeItem(DB_SETTING_KEY);
      })
      .finally(() => setTablesLoading(false));
  }, [canDb]);

  const filteredTables = tables.filter((t) => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return true;
    const purpose = tablePurpose(t, locale) || '';
    return (
      String(t.name || '')
        .toLowerCase()
        .includes(q) ||
      purpose.toLowerCase().includes(q) ||
      String(t.description || '')
        .toLowerCase()
        .includes(q) ||
      String(t.description_ko || '')
        .toLowerCase()
        .includes(q) ||
      String(t.category || '')
        .toLowerCase()
        .includes(q)
    );
  });

  const handleReauth = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Please enter your password.');
      return;
    }
    setReauthLoading(true);
    try {
      await api.post('/settings/db/reauth', { password });
      sessionStorage.setItem(DB_SETTING_KEY, JSON.stringify({ at: Date.now() }));
      setUnlocked(true);
      setPassword('');
      toast.success('Authenticated.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid password.');
    } finally {
      setReauthLoading(false);
    }
  };

  const handleBackupThen = async (tableName, next) => {
    try {
      await api.post('/settings/db/backup', { tableName });
      setBackupDone(true);
      toast.success('Backup completed.');
      next();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Backup failed');
    }
  };

  const loadTableDataAndStructure = (name) => {
    setDataLoading(true);
    Promise.all([
      api.get(`/settings/db/tables/${name}/data`),
      api.get(`/settings/db/tables/${name}/structure`)
    ])
      .then(([dataRes, structRes]) => {
        setTableData(dataRes.data.data || []);
        setTableStructure(structRes.data.data || []);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setDataLoading(false));
  };

  const handleSelectData = () => {
    setChoiceMode('data');
    setSelectedTable(selectedTable);
    if (!backupDone) {
      handleBackupThen(selectedTable.name, () => loadTableDataAndStructure(selectedTable.name));
    } else {
      loadTableDataAndStructure(selectedTable.name);
    }
  };

  const handleSelectStructure = () => {
    setChoiceMode('structure');
    if (!backupDone) {
      handleBackupThen(selectedTable.name, () => loadTableStructure(selectedTable.name));
    } else {
      loadTableStructure(selectedTable.name);
    }
  };

  const loadTableData = (name) => {
    setDataLoading(true);
    api.get(`/settings/db/tables/${name}/data`)
      .then((res) => setTableData(res.data.data || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load data'))
      .finally(() => setDataLoading(false));
  };

  const loadTableStructure = (name) => {
    setDataLoading(true);
    api.get(`/settings/db/tables/${name}/structure`)
      .then((res) => setTableStructure(res.data.data || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load structure'))
      .finally(() => setDataLoading(false));
  };

  const closeDetail = () => {
    setChoiceMode(null);
    setSelectedTable(null);
    setTableData([]);
    setTableStructure([]);
    setBackupDone(false);
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingRow(null);
    setShowDeleteConfirm(false);
    setDeletingRow(null);
  };

  const pkColumns = (tableStructure || []).filter((c) => c.COLUMN_KEY === 'PRI').map((c) => c.COLUMN_NAME);

  const handleAddSave = async (body) => {
    await api.post(`/settings/db/tables/${selectedTable.name}/rows`, body);
    toast.success('Row added');
    loadTableDataAndStructure(selectedTable.name);
  };

  const handleEditSave = async (body) => {
    await api.put(`/settings/db/tables/${selectedTable.name}/rows`, body);
    toast.success('Row updated');
    setShowEditModal(false);
    setEditingRow(null);
    loadTableDataAndStructure(selectedTable.name);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRow || !selectedTable) return;
    const pk = {};
    pkColumns.forEach((col) => { pk[col] = deletingRow[col]; });
    await api.delete(`/settings/db/tables/${selectedTable.name}/rows`, { data: pk });
    toast.success('Row deleted');
    setShowDeleteConfirm(false);
    setDeletingRow(null);
    loadTableDataAndStructure(selectedTable.name);
  };

  if (!canDb) {
    return null;
  }

  if (!unlocked) {
    return (
      <div>
        <ModulePageHeading
          resolveNav={resolveSettingsNav}
          title="DB Setting"
          infoTitle="DB Setting"
          infoChildren={
            <p>
              Browse every database table name and its purpose below. Enter your admin password only when you need to
              edit rows or structure.
            </p>
          }
        />

        <div className="bg-card rounded-lg shadow p-6 max-w-md mb-6">
          <div className="flex items-center gap-2 text-gray-700 mb-4">
            <Lock className="w-5 h-5" />
            Unlock edit tools
          </div>
          <form onSubmit={handleReauth}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              placeholder="Admin password"
              autoFocus
            />
            <button
              type="submit"
              disabled={reauthLoading}
              className="w-full py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {reauthLoading ? 'Verifying...' : 'Unlock Edit data / structure'}
            </button>
          </form>
        </div>

        <DbTableCatalogue
          tables={filteredTables}
          loading={tablesLoading}
          filter={tableFilter}
          onFilterChange={setTableFilter}
          unlocked={false}
        />
      </div>
    );
  }

  if (choiceMode === 'data' && selectedTable) {
    const columns = tableData.length > 0 ? Object.keys(tableData[0]) : (tableStructure.map((c) => c.COLUMN_NAME));
    return (
      <div>
        <ModulePageHeading
          resolveNav={resolveSettingsNav}
          title={selectedTable.name}
          showBack
          onBack={closeDetail}
          backLabel="Back to table list"
          infoTitle={selectedTable.name}
          infoChildren={<p>Edit table rows. Back up before changing data.</p>}
          actions={
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add row
            </button>
          }
        />
        {dataLoading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {columns.map((col) => (
                    <th key={col} className="px-3 py-2 text-left font-medium text-gray-700">{col}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium text-gray-700 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    {columns.map((col) => (
                      <td key={col} className="px-3 py-2 text-gray-900">{String(row[col] ?? '')}</td>
                    ))}
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => { setEditingRow(row); setShowEditModal(true); }}
                        className="text-blue-600 hover:text-blue-800 mr-2"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4 inline" />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDeletingRow(row); setShowDeleteConfirm(true); }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-xs text-gray-500">Up to 500 rows. Add / Edit / Delete with buttons above.</p>

        {showAddModal && (
          <RowFormModal
            tableName={selectedTable.name}
            structure={tableStructure}
            mode="add"
            onSave={handleAddSave}
            onClose={() => setShowAddModal(false)}
          />
        )}
        {showEditModal && editingRow && (
          <RowFormModal
            tableName={selectedTable.name}
            structure={tableStructure}
            mode="edit"
            initialValues={editingRow}
            onSave={handleEditSave}
            onClose={() => { setShowEditModal(false); setEditingRow(null); }}
          />
        )}
        {showDeleteConfirm && deletingRow && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
            <div className="bg-card rounded-lg shadow-xl p-4 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-2">Delete row?</h3>
              <p className="text-sm text-gray-600 mb-2">
                Primary key: {pkColumns.map((col) => `${col}=${deletingRow[col]}`).join(', ')}
              </p>
              <p className="text-sm text-red-600 mb-4">This cannot be undone.</p>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowDeleteConfirm(false); setDeletingRow(null); }} className="px-4 py-2 border rounded hover:bg-gray-50">
                  Cancel
                </button>
                <button type="button" onClick={handleDeleteConfirm} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveSettingsNav}
        title="DB Setting"
        infoTitle="DB Setting"
        infoChildren={
          <p>
            Catalogue of all MySQL tables and what they store. Use Edit data / Edit table structure only after unlock
            (backup runs before data edit).
          </p>
        }
      />

      <DbTableCatalogue
        tables={filteredTables}
        loading={tablesLoading}
        filter={tableFilter}
        onFilterChange={setTableFilter}
        unlocked
        onEditData={(t) => {
          setSelectedTable(t);
          setChoiceMode(null);
          setBackupDone(false);
          handleBackupThen(t.name, () => {
            setChoiceMode('data');
            loadTableDataAndStructure(t.name);
          });
        }}
        onEditStructure={(t) => navigate(`/settings/db/structure/${t.name}`)}
      />
    </div>
  );
}


export default DbSetting;
